import DataOps from './DataOps';

interface EmailTemplate {
    subject: string;
    body: string;
    htmlBody?: string;
}

class EmailService {
    /**
     * Send an email using a third-party service (e.g., SendGrid, AWS SES, Mailgun)
     * In production, this would call your email API endpoint
     */
    static async sendEmail(
        to: string,
        subject: string,
        body: string,
        htmlBody?: string
    ): Promise<boolean> {
        try {
            // TODO: Replace with actual email service API call
            // Example with SendGrid:
            // const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
            //     method: 'POST',
            //     headers: {
            //         'Authorization': `Bearer ${process.env.SENDGRID_API_KEY}`,
            //         'Content-Type': 'application/json'
            //     },
            //     body: JSON.stringify({
            //         personalizations: [{ to: [{ email: to }] }],
            //         from: { email: 'notifications@snapcamp.ai', name: 'SnapCamp' },
            //         subject: subject,
            //         content: [
            //             { type: 'text/plain', value: body },
            //             { type: 'text/html', value: htmlBody || body }
            //         ]
            //     })
            // });
            return true;
        } catch (error) {
            console.error('Error sending email:', error);
            return false;
        }
    }

    /**
     * Get user notification preferences from storage
     */
    static async getUserNotificationSettings(userId: string): Promise<any> {
        try {
            // First try to get settings from user-specific storage
            const settings = await DataOps.retrieveDataFromStorage(`notificationSettings_${userId}`);
            
            // If no user-specific settings, fall back to general settings
            if (!settings) {
                const generalSettings = await DataOps.retrieveDataFromStorage('notificationSettings');
                return generalSettings || {
                    emailNotifications: true,
                    pushNotifications: true,
                    smsNotifications: false,
                    marketingEmails: true,
                    sessionReminders: true,
                    newSessionAlerts: true,
                };
            }
            
            return settings;
        } catch (error) {
            console.error('Error fetching notification settings:', error);
            // Return default settings if error
            return {
                emailNotifications: true,
                pushNotifications: true,
                smsNotifications: false,
                marketingEmails: true,
                sessionReminders: true,
                newSessionAlerts: true,
            };
        }
    }

    /**
     * Get user email from Firebase
     */
    static async getUserEmail(userId: string): Promise<string | null> {
        try {
            const user = await DataOps.fetchDataFromFirebase('user', userId);
            return user?.email || null;
        } catch (error) {
            console.error('Error fetching user email:', error);
            return null;
        }
    }

    /**
     * Send registration notification email
     */
    static async sendRegistrationEmail(
        userId: string,
        childNames: string[],
        parentName: string,
        sessionTitle: string,
        action: 'registered' | 'unregistered'
    ): Promise<boolean> {
        const settings = await this.getUserNotificationSettings(userId);
        
        if (!settings.emailNotifications) {
            return false;
        }

        const userEmail = await this.getUserEmail(userId);
        if (!userEmail) {
            return false;
        }

        const childListText = childNames.length === 1 
            ? childNames[0]
            : childNames.length === 2
            ? `${childNames[0]} and ${childNames[1]}`
            : `${childNames.slice(0, -1).join(', ')}, and ${childNames[childNames.length - 1]}`;

        const subject = `Registration ${action === 'registered' ? 'Confirmed' : 'Cancelled'} - ${sessionTitle}`;
        const body = `Hello,\n\n${parentName} has ${action} ${childListText} for "${sessionTitle}".\n\nThank you,\nSnapCamp Team`;
        const htmlBody = `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #34C759;">Registration ${action === 'registered' ? 'Confirmed' : 'Cancelled'}</h2>
                <p>Hello,</p>
                <p><strong>${parentName}</strong> has ${action} <strong>${childListText}</strong> for <strong>"${sessionTitle}"</strong>.</p>
                <hr style="border: 1px solid #e0e0e0; margin: 20px 0;" />
                <p style="color: #666; font-size: 12px;">
                    Thank you,<br />
                    SnapCamp Team
                </p>
            </div>
        `;

        return await this.sendEmail(userEmail, subject, body, htmlBody);
    }

    /**
     * Send attendance/check-in notification email
     */
    static async sendAttendanceEmail(
        userId: string,
        childName: string,
        sessionTitle: string,
        date: string
    ): Promise<boolean> {
        const settings = await this.getUserNotificationSettings(userId);
        
        if (!settings.emailNotifications || !settings.sessionReminders) {
            return false;
        }

        const userEmail = await this.getUserEmail(userId);
        if (!userEmail) {
            return false;
        }

        const subject = `${childName} Checked In - ${sessionTitle}`;
        const body = `Hello,\n\n${childName} has been checked in for "${sessionTitle}" on ${date}.\n\nThank you,\nSnapCamp Team`;
        const htmlBody = `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #34C759;">Check-In Confirmed ✓</h2>
                <p>Hello,</p>
                <p><strong>${childName}</strong> has been checked in for <strong>"${sessionTitle}"</strong> on <strong>${date}</strong>.</p>
                <hr style="border: 1px solid #e0e0e0; margin: 20px 0;" />
                <p style="color: #666; font-size: 12px;">
                    Thank you,<br />
                    SnapCamp Team
                </p>
            </div>
        `;

        return await this.sendEmail(userEmail, subject, body, htmlBody);
    }

    /**
     * Send comment notification email
     */
    static async sendCommentEmail(
        userId: string,
        childName: string,
        coachName: string,
        sessionTitle: string,
        date: string
    ): Promise<boolean> {
        const settings = await this.getUserNotificationSettings(userId);
        
        if (!settings.emailNotifications) {
            return false;
        }

        const userEmail = await this.getUserEmail(userId);
        if (!userEmail) {
            return false;
        }

        const subject = `New Comment for ${childName} - ${sessionTitle}`;
        const body = `Hello,\n\n${coachName} added a comment for ${childName} in "${sessionTitle}" on ${date}.\n\nLog in to SnapCamp to view the comment.\n\nThank you,\nSnapCamp Team`;
        const htmlBody = `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #34C759;">New Comment 💬</h2>
                <p>Hello,</p>
                <p><strong>${coachName}</strong> added a comment for <strong>${childName}</strong> in <strong>"${sessionTitle}"</strong> on <strong>${date}</strong>.</p>
                <p><a href="https://snapcamp.ai" style="background-color: #34C759; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 10px;">View Comment</a></p>
                <hr style="border: 1px solid #e0e0e0; margin: 20px 0;" />
                <p style="color: #666; font-size: 12px;">
                    Thank you,<br />
                    SnapCamp Team
                </p>
            </div>
        `;

        return await this.sendEmail(userEmail, subject, body, htmlBody);
    }

    /**
     * Send photo notification email
     */
    static async sendPhotoEmail(
        userId: string,
        childName: string,
        coachName: string,
        sessionTitle: string,
        date: string
    ): Promise<boolean> {
        const settings = await this.getUserNotificationSettings(userId);
        
        if (!settings.emailNotifications) {
            return false;
        }

        const userEmail = await this.getUserEmail(userId);
        if (!userEmail) {
            return false;
        }

        const subject = `New Moment Shared for ${childName} - ${sessionTitle}`;
        const body = `Hello,\n\n${coachName} shared a moment for ${childName} in "${sessionTitle}" on ${date}.\n\nLog in to SnapCamp to view the photo.\n\nThank you,\nSnapCamp Team`;
        const htmlBody = `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #34C759;">New Moment Shared 📸</h2>
                <p>Hello,</p>
                <p><strong>${coachName}</strong> shared a moment for <strong>${childName}</strong> in <strong>"${sessionTitle}"</strong> on <strong>${date}</strong>.</p>
                <p><a href="https://snapcamp.ai" style="background-color: #34C759; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 10px;">View Photo</a></p>
                <hr style="border: 1px solid #e0e0e0; margin: 20px 0;" />
                <p style="color: #666; font-size: 12px;">
                    Thank you,<br />
                    SnapCamp Team
                </p>
            </div>
        `;

        return await this.sendEmail(userEmail, subject, body, htmlBody);
    }

    /**
     * Send new session notification email
     */
    static async sendNewSessionEmail(
        userId: string,
        campName: string,
        sessionTitle: string,
        sessionStartDate: string
    ): Promise<boolean> {
        const settings = await this.getUserNotificationSettings(userId);
        
        if (!settings.emailNotifications || !settings.newSessionAlerts) {
            return false;
        }

        const userEmail = await this.getUserEmail(userId);
        if (!userEmail) {
            return false;
        }

        const subject = `New Session at ${campName}`;
        const body = `Hello,\n\n${campName} published a new session: "${sessionTitle}" starting ${sessionStartDate}.\n\nLog in to SnapCamp to register.\n\nThank you,\nSnapCamp Team`;
        const htmlBody = `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #34C759;">New Session Available! 🎪</h2>
                <p>Hello,</p>
                <p><strong>${campName}</strong> published a new session:</p>
                <div style="background-color: #f8f8f8; padding: 15px; border-radius: 8px; margin: 15px 0;">
                    <h3 style="margin: 0 0 10px 0; color: #333;">${sessionTitle}</h3>
                    <p style="margin: 0; color: #666;">Starting ${sessionStartDate}</p>
                </div>
                <p><a href="https://snapcamp.ai" style="background-color: #34C759; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 10px;">Register Now</a></p>
                <hr style="border: 1px solid #e0e0e0; margin: 20px 0;" />
                <p style="color: #666; font-size: 12px;">
                    Thank you,<br />
                    SnapCamp Team
                </p>
            </div>
        `;

        return await this.sendEmail(userEmail, subject, body, htmlBody);
    }

    /**
     * Send marketing email (newsletter, promotions, etc.)
     */
    static async sendMarketingEmail(
        userId: string,
        subject: string,
        body: string,
        htmlBody?: string
    ): Promise<boolean> {
        const settings = await this.getUserNotificationSettings(userId);
        
        if (!settings.emailNotifications || !settings.marketingEmails) {
            return false;
        }

        const userEmail = await this.getUserEmail(userId);
        if (!userEmail) {
            return false;
        }

        return await this.sendEmail(userEmail, subject, body, htmlBody);
    }
}

export default EmailService;

